import React, { useState, useEffect } from 'react';
import {
  FaUsers,
  FaPlus,
  FaEdit,
  FaTrash,
  FaFilter,
  FaUserShield,
  FaProjectDiagram,
  FaEye,
  FaToggleOn,
  FaToggleOff
} from 'react-icons/fa';
import CompactActionBar from '../components/CompactActionBar';
import { systemUserService, SystemUser, SystemUserFilters } from '../services/systemUserService';
import { usePageTitle } from '../hooks/usePageTitle';
import { PermissionGuard, usePermissions } from '../components/PermissionGuard';
import ConfirmDialog from '../components/ConfirmDialog';
import '../css/standard-components.css';
import { useNavigate } from 'react-router-dom';

const SystemUsers: React.FC = () => {
  usePageTitle();
  const navigate = useNavigate();
  const { canAccess, getAccessibleProjectIds } = usePermissions();
  const [users, setUsers] = useState<SystemUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);
  
  // Filters
  const [filters, setFilters] = useState<SystemUserFilters>({
    search: '',
    isActive: undefined,
    page: 1,
    limit: 100
  });

  // Delete confirmation
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState<SystemUser | null>(null);

  useEffect(() => {
    loadUsers();
  }, [filters]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await systemUserService.getSystemUsers(filters);
      setUsers(response.users);
      setCurrentPage(response.pagination.page);
      setTotalPages(response.pagination.totalPages);
      setTotalUsers(response.pagination.total);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (search: string) => {
    setFilters({ ...filters, search, page: 1 });
    setCurrentPage(1);
  };

  const handleFilterChange = (key: keyof SystemUserFilters, value: any) => {
    setFilters({ ...filters, [key]: value, page: 1 });
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setFilters({ ...filters, page });
  };

  const handleDelete = (user: SystemUser) => {
    setUserToDelete(user);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (userToDelete) {
      try {
        setDeleting(true);
        await systemUserService.deleteSystemUser(userToDelete.id);
        loadUsers();
        setShowDeleteDialog(false);
        setUserToDelete(null);
      } catch (error) {
        console.error('Error deleting user:', error);
        alert(error instanceof Error ? error.message : 'Failed to delete user');
      } finally {
        setDeleting(false);
      }
    }
  };

  const handleEdit = (user: SystemUser) => {
    navigate(`/auth/user/edit?id=${user.id}`);
  };

  const handleView = (user: SystemUser) => {
    navigate(`/auth/user/view?id=${user.id}`);
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      isActive: undefined,
      page: 1,
      limit: 10
    });
  };

  return (
    <div className="page-container">
      {/* Action Bar */}
      <CompactActionBar>
        <div style={{ display: "flex", gap: "12px", alignItems: "center", flex: 1 }}>
          <input
            type="text"
            placeholder="Search users by username or employee name..."
            value={filters.search || ''}
            onChange={(e) => handleSearch(e.target.value)}
            style={{
              padding: "6px 12px",
              border: "1px solid #e5e7eb",
              borderRadius: "6px",
              fontSize: "13px",
              minWidth: "400px",
            }}
          />

          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={{ fontSize: "13px", color: "#6b7280" }}>Status:</span>
            <select
              value={filters.isActive === undefined ? '' : filters.isActive.toString()}
              onChange={(e) => handleFilterChange('isActive', e.target.value === '' ? undefined : e.target.value === 'true')}
              style={{
                padding: "6px 10px",
                border: "1px solid #e5e7eb",
                borderRadius: "6px",
                fontSize: "13px",
              }}
            >
              <option value="">All Status</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={{ fontSize: "13px", color: "#6b7280" }}>Show:</span>
            <select
              value={filters.limit}
              onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
              style={{
                padding: "6px 10px",
                border: "1px solid #e5e7eb",
                borderRadius: "6px",
                fontSize: "13px",
              }}
            >
              <option value={100}>100</option>
              <option value={200}>200</option>
              <option value={300}>300</option>
              <option value={400}>400</option>
              <option value={500}>500</option>
            </select>
          </div>

          <button 
            onClick={clearFilters} 
            style={{
              padding: "6px 12px",
              border: "1px solid #e5e7eb",
              borderRadius: "6px",
              fontSize: "13px",
              background: "#fff",
              color: "#6b7280",
              cursor: "pointer",
            }}
          >
            Clear
          </button>

          <PermissionGuard permission="user.create">
            <button 
              className="btn btn-primary"
              onClick={() => navigate('/auth/user/add')}
              style={{ marginLeft: 'auto', padding: '6px 12px', fontSize: '13px' }}
            >
              <FaPlus /> Add User
            </button>
          </PermissionGuard>
        </div>
      </CompactActionBar>

      {loading ? (
        <div style={{ background: '#fff', padding: '60px', textAlign: 'center' }}>
          <div style={{fontSize: '16px', color: '#6b7280'}}>Loading users...</div>
        </div>
      ) : (
        <div style={{ background: '#fff', margin: 0, padding: 0 }}>
          <table className="standard-table" style={{ margin: 0 }}>
            <thead>
              <tr>
                <th>Sr. No</th>
                <th>Employee Name</th>
                <th>Username</th>
                <th>Email</th>
                <th>Status</th>
                <th>Projects</th>
                <th>Roles</th>
                <th>Created Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users?.map((user, index) => {
                const projectRoles = systemUserService.getUserProjectRoles(user);
                
                return (
                  <tr key={user.id}>
                    <td style={{ fontWeight: "600", color: "#4f46e5" }}>
                      {(users?.length || 0) - index}
                    </td>
                    <td>
                      <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                        <FaUsers style={{color: '#4f46e5', fontSize: '14px'}} />
                        <span style={{fontWeight: '500'}}>{user.employeeName}</span>
                      </div>
                    </td>
                    <td>
                      <span style={{fontFamily: 'monospace', background: '#f3f4f6', padding: '2px 6px', borderRadius: '4px', fontSize: '12px'}}>
                        {user.username}
                      </span>
                    </td>
                    <td>
                      <span style={{color: '#6b7280'}}>{user.email || 'Not provided'}</span>
                    </td>
                    <td>
                      {user.isActive ? (
                        <span className="status-badge status-active">
                          Active
                        </span>
                      ) : (
                        <span className="status-badge status-inactive">
                          Inactive
                        </span>
                      )}
                    </td>
                    <td>
                      <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                        <FaProjectDiagram style={{color: '#10b981', fontSize: '12px'}} />
                        <span style={{fontSize: '14px'}}>{systemUserService.getUserProjectCount(user)} projects</span>
                      </div>
                    </td>
                    <td>
                      <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                        <FaUserShield style={{color: '#f59e0b', fontSize: '12px'}} />
                        <span style={{fontSize: '14px'}}>{systemUserService.getUserRoleCount(user)} roles</span>
                      </div>
                    </td>
                    <td>
                      <span style={{color: '#6b7280', fontSize: '14px'}}>
                        {new Date(user.createdAt).toLocaleDateString()}
                      </span>
                    </td>
                    <td>
                      <div className="table-actions">
                        <button
                          className="btn btn-sm btn-info"
                          onClick={() => handleView(user)}
                          title="View User"
                        >
                          <FaEye />
                        </button>
                        <PermissionGuard permission="user.edit">
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => handleEdit(user)}
                            title="Edit User"
                          >
                            <FaEdit />
                          </button>
                        </PermissionGuard>
                        <PermissionGuard permission="user.delete">
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDelete(user)}
                            title="Delete User"
                          >
                            <FaTrash />
                          </button>
                        </PermissionGuard>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {(users?.length === 0 && !loading) && (
            <div style={{textAlign: 'center', padding: '60px', color: '#6b7280'}}>
              <FaUsers style={{fontSize: '48px', marginBottom: '16px', opacity: '0.5'}} />
              <h3 style={{margin: '0 0 8px 0', color: '#374151'}}>No users found</h3>
              <p style={{margin: '0 0 24px 0'}}>Create your first user to get started with user management.</p>
              <PermissionGuard permission="user.create">
                <button 
                  className="btn btn-primary"
                  onClick={() => navigate('/auth/user/add')}
                >
                  <FaPlus /> Add User
                </button>
              </PermissionGuard>
            </div>
          )}
        </div>
      )}

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="content-section">
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
            <div style={{color: '#6b7280', fontSize: '14px'}}>
              Showing {((currentPage - 1) * 10) + 1} to {Math.min(currentPage * 10, totalUsers)} of {totalUsers} users
            </div>
            <div style={{display: 'flex', gap: '8px', alignItems: 'center'}}>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let page: number;
                if (totalPages <= 5) {
                  page = i + 1;
                } else if (currentPage <= 3) {
                  page = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  page = totalPages - 4 + i;
                } else {
                  page = currentPage - 2 + i;
                }
                
                return (
                  <button
                    key={page}
                    className={`btn btn-sm ${page === currentPage ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </button>
                );
              })}
              
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        title="Delete User"
        message={`Are you sure you want to delete the user "${userToDelete?.employeeName}" (${userToDelete?.username})? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDelete}
        onCancel={() => setShowDeleteDialog(false)}
        type="danger"
      />
    </div>
  );
};

export default SystemUsers;
